// src/components/common/Navbar.tsx - Korrigierte Version
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import { Bars3Icon as MenuIcon, XMarkIcon as XIcon, UserCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { isAuthenticated, logout, getDisplayName, isAdmin as checkIsAdmin } from '../../services/authService';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../../contexts/LanguageContext';

const Navbar: React.FC = () => {
  const { t } = useLanguage();
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const navigate = useNavigate();

  // Check authentication status whenever the component renders
  useEffect(() => {
    const checkAuth = async () => {
      const authStatus = isAuthenticated();
      setAuthenticated(authStatus);
      if (authStatus) {
        setDisplayName(getDisplayName());
        
        // Check if user is admin using the isAdmin function
        const adminStatus = checkIsAdmin();
        setIsAdmin(adminStatus);
      } else {
        setDisplayName(null);
        setIsAdmin(false);
      }
    };

    checkAuth();
    
    // Setup event listener for storage changes (for logout in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'destiny_auth' || e.key === null) {
        checkAuth();
      }
    };
    
    // Listen for auth-state-changed events from login/logout operations
    const handleAuthStateChanged = () => {
      console.log('Auth state change detected in Navbar');
      checkAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-state-changed', handleAuthStateChanged);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-state-changed', handleAuthStateChanged);
    };
  }, []);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      
      // Call the logout function from authService
      await logout();
      
      // Update local state
      setAuthenticated(false);
      setDisplayName(null);
      setIsAdmin(false);
      
      // Redirect to home page
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Define navigation with translated labels
  let navigation = [
    { name: t('nav.dashboard'), href: '/' },
    { name: t('nav.achievements'), href: '/achievements' },
    { name: t('nav.characters'), href: '/characters' }
  ];

  // Add admin link for admins
  if (isAdmin) {
    navigation = [...navigation, { name: t('nav.admin'), href: '/admin' }];
  }

  return (
    <Disclosure as="nav" className="bg-gray-800">
      {({ open }) => (
        <>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Link to="/" className="text-xl font-bold text-destiny-gold">
                    Destiny Achievement Helper
                  </Link>
                </div>
                <div className="hidden md:block">
                  <div className="ml-10 flex items-baseline space-x-4">
                    {navigation.map((item) => {
                      const href = item.name === t('nav.achievements') ? '/achievements?reset=1' : item.href;
                      return (
                        <Link
                          key={item.name}
                          to={href}
                          className={`${
                            item.name === t('nav.admin') 
                              ? 'text-destiny-gold hover:bg-gray-700 hover:text-destiny-gold' 
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          } px-3 py-2 rounded-md text-sm font-medium ${
                            item.name === t('nav.admin') ? 'flex items-center' : ''
                          }`}
                        >
                          {item.name === t('nav.admin') && <ShieldCheckIcon className="h-5 w-5 mr-1" />}
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="ml-4 flex items-center md:ml-6">
                  <LanguageSelector />
                  {authenticated ? (
                    <div className="flex items-center space-x-4">
                      {displayName && (
                        <span className="text-gray-300 text-sm">
                          <UserCircleIcon className="inline h-4 w-4 mr-1" />
                          {displayName}
                        </span>
                      )}
                      <button
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        className={`text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium ${
                          isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {isLoggingOut ? t('auth.loggingOut') : t('nav.logout')}
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                    >
                      {t('nav.login')}
                    </Link>
                  )}
                </div>
              </div>
              
              {/* WICHTIGER FIX: Der mobile Menü-Toggle wird immer gerendert */}
              <div className="-mr-2 flex md:hidden">
                <Disclosure.Button className="bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* WICHTIGER FIX: Die Mobile-Menü-Struktur bleibt konsistent, unabhängig vom Auth-Status */}
          <Disclosure.Panel className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* Navigationslinks werden immer angezeigt */}
              {navigation.map((item) => {
                const href = item.name === t('nav.achievements') ? '/achievements?reset=1' : item.href;
                return (
                  <Link
                    key={item.name}
                    to={href}
                    className={`${
                      item.name === t('nav.admin')
                        ? 'text-destiny-gold hover:bg-gray-700 hover:text-destiny-gold flex items-center'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white block'
                    } px-3 py-2 rounded-md text-base font-medium`}
                  >
                    {item.name === t('nav.admin') && <ShieldCheckIcon className="h-5 w-5 mr-1" />}
                    {item.name}
                  </Link>
                );
              })}
              
              {/* Sprachauswahl wird immer angezeigt */}
              <div className="py-2">
                <LanguageSelector />
              </div>
              
              {/* Auth-abhängiger Inhalt mit konsistenter Höhe */}
              <div className="min-h-[40px] py-2"> {/* Minimale Höhe sicherstellen */}
                {authenticated ? (
                  <>
                    {displayName && (
                      <div className="text-gray-300 px-3 py-2 block">
                        <UserCircleIcon className="inline h-5 w-5 mr-1" />
                        {displayName}
                      </div>
                    )}
                    <button
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className={`text-gray-300 hover:bg-gray-700 hover:text-white block w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                        isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isLoggingOut ? t('auth.loggingOut') : t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="text-gray-300 hover:bg-gray-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
                  >
                    {t('nav.login')}
                  </Link>
                )}
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default Navbar;